import { Box, IconButton, MenuItem, Select, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

interface PaginationProps {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  onPage: (page: number) => void;
  onPageSize: (size: number) => void;
}

export function Pagination({
  page,
  pageSize,
  totalItems,
  totalPages,
  onPage,
  onPageSize,
}: PaginationProps) {
  const from = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, totalItems);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 0.75 }}>
      <Typography variant="caption" color="text.secondary">
        {from.toLocaleString()}–{to.toLocaleString()} of {totalItems.toLocaleString()}
      </Typography>
      <Box sx={{ flex: 1 }} />
      <Typography variant="caption" color="text.secondary">
        Rows
      </Typography>
      <Select
        size="small"
        value={pageSize}
        onChange={(e) => onPageSize(Number(e.target.value))}
        sx={{ height: 30 }}
        /*
         * The "Rows" beside it is a sibling, not a label, so this control had no
         * accessible name — a screen reader announced "combobox, 20" with no
         * indication of what changing it would do.
         */
        inputProps={{ 'aria-label': 'Rows per page' }}
      >
        {[10, 20, 50, 100].map((n) => (
          <MenuItem key={n} value={n}>
            {n}
          </MenuItem>
        ))}
      </Select>
      <IconButton
        size="small"
        disabled={page <= 1}
        onClick={() => onPage(page - 1)}
        aria-label="Previous page"
      >
        <ChevronLeftIcon fontSize="small" />
      </IconButton>
      <Typography variant="caption" sx={{ minWidth: 74, textAlign: 'center' }}>
        Page {page} / {Math.max(1, totalPages)}
      </Typography>
      <IconButton
        size="small"
        disabled={page >= totalPages}
        onClick={() => onPage(page + 1)}
        aria-label="Next page"
      >
        <ChevronRightIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}
